import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, X, Paperclip, FileText, Eye, Lock } from "lucide-react";
import { Link } from "react-router-dom";

const STATUSES = ["todo", "in_progress", "done"];

const STATUS_STYLE = {
  todo: { label: "Todo", bg: "bg-amber-50", text: "text-amber-800", border: "border-amber-300", dot: "bg-amber-500" },
  in_progress: { label: "In Progress", bg: "bg-blue-50", text: "text-blue-800", border: "border-blue-300", dot: "bg-blue-500" },
  done: { label: "Done", bg: "bg-green-50", text: "text-green-800", border: "border-green-300", dot: "bg-green-500" },
};

export default function Tasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [scope, setScope] = useState("mine"); // mine | team
  const [form, setForm] = useState({ title: "", description: "", assignee_id: "", priority: "normal", due_date: "" });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const isAdmin = user?.role === "admin";
    const params = isAdmin ? {} : { scope };
    const [t, u] = await Promise.all([
      api.get("/tasks", { params }),
      api.get("/users").catch(() => ({ data: [] })),
    ]);
    setTasks(t.data);
    setUsers(u.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

  const create = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data: created } = await api.post("/tasks", { ...form, assignee_id: form.assignee_id || user.id });
      // Upload attached files (if any)
      if (files.length > 0) {
        await Promise.all(files.map((f) => {
          const fd = new FormData();
          fd.append("file", f);
          fd.append("task_id", created.id);
          return api.post("/documents/upload", fd);
        }));
      }
      toast.success(files.length > 0 ? `Task created with ${files.length} attachment${files.length > 1 ? "s" : ""}` : "Task created");
      setForm({ title: "", description: "", assignee_id: "", priority: "normal", due_date: "" });
      setFiles([]);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const setStatus = async (id, status) => {
    await api.patch(`/tasks/${id}`, { status });
    load();
  };

  const grouped = STATUSES.reduce((acc, s) => {
    acc[s] = tasks.filter((t) => t.status === s);
    return acc;
  }, {});

  const userMap = Object.fromEntries(users.map((u) => [u.id, u.name]));

  return (
    <div className="min-h-screen" data-testid="tasks-page">
      <header className="border-b border-zinc-200 page-px py-6 sm:py-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-uppercase mb-2">Operations</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tighter" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>Tasks</h1>
        </div>
        <button
          onClick={() => setShowForm(true)}
          data-testid="new-task-button"
          className="bg-ikb text-white px-4 py-2.5 text-sm font-medium hover:bg-[#00227A] flex items-center gap-2"
        >
          <Plus size={14} /> New Task
        </button>
      </header>

      {/* Scope tabs (employees only) */}
      {user?.role === "employee" && (
        <div className="border-b border-zinc-200 page-px flex gap-0" data-testid="task-scope-tabs">
          {[
            { id: "mine", label: "My Tasks" },
            { id: "team", label: "Team Tasks" },
          ].map((s) => (
            <button
              key={s.id}
              onClick={() => setScope(s.id)}
              data-testid={`tab-${s.id}`}
              className={`px-5 py-3 text-xs uppercase tracking-wider border-b-2 -mb-px transition-colors ${
                scope === s.id
                  ? "border-[#002FA7] text-[#002FA7] font-semibold"
                  : "border-transparent text-zinc-500 hover:text-zinc-900"
              }`}
            >
              {s.label}
              {scope === s.id && (
                <span className="ml-2 text-[10px] font-mono text-zinc-500">{tasks.length}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {scope === "team" && user?.role === "employee" && (
        <div className="page-px py-2.5 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 flex items-center gap-2">
          <Eye size={12} /> Read-only view. You can see other employees' tasks but cannot modify them.
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-zinc-300 w-full max-w-lg p-8" data-testid="task-form">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold tracking-tight">New Task</h2>
              <button onClick={() => setShowForm(false)}><X size={18} /></button>
            </div>
            <form onSubmit={create} className="space-y-4">
              <Field label="Title" required>
                <input data-testid="task-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input" required />
              </Field>
              <Field label="Description">
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input min-h-[80px]" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Assignee">
                  <select value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })} className="input">
                    <option value="">— Me —</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </Field>
                <Field label="Priority">
                  <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="input">
                    <option value="normal">Normal</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </Field>
              </div>
              <Field label="Due Date">
                <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="input" />
              </Field>
              <Field label={`Attachments${files.length ? ` · ${files.length}` : ""}`}>
                <label className="flex items-center gap-2 border border-dashed border-zinc-300 px-3 py-2.5 cursor-pointer hover:bg-zinc-50 hover:border-[#002FA7]" data-testid="task-form-attach">
                  <Paperclip size={14} className="text-zinc-500" />
                  <span className="text-xs text-zinc-600 flex-1">{files.length === 0 ? "Click to add files (multiple)" : `${files.length} file(s) selected`}</span>
                  <input
                    type="file"
                    multiple
                    onChange={(e) => setFiles([...files, ...Array.from(e.target.files || [])])}
                    className="hidden"
                  />
                </label>
                {files.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {files.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs border border-zinc-200 px-2 py-1.5 bg-zinc-50">
                        <FileText size={11} className="text-zinc-500 shrink-0" />
                        <span className="truncate flex-1">{f.name}</span>
                        <span className="text-[10px] font-mono text-zinc-400">{(f.size / 1024).toFixed(1)} KB</span>
                        <button
                          type="button"
                          onClick={() => setFiles(files.filter((_, j) => j !== i))}
                          className="text-zinc-400 hover:text-red-600"
                        ><X size={11} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </Field>
              <button type="submit" disabled={submitting} data-testid="task-submit-button" className="w-full bg-ikb text-white py-2.5 text-sm font-medium disabled:opacity-50">
                {submitting ? "Creating…" : "Create Task"}
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 border-b border-zinc-200">
        {STATUSES.map((s, i) => {
          const style = STATUS_STYLE[s];
          return (
          <div key={s} className={`p-6 ${i > 0 ? "border-l border-zinc-200" : ""} min-h-[400px]`} data-testid={`column-${s}`}>
            <div className="flex items-center justify-between mb-4 pb-3 border-b-2 border-zinc-200">
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${style.dot}`} />
                <span className={`text-xs font-semibold tracking-[0.12em] uppercase ${style.text}`}>{style.label}</span>
              </div>
              <span className={`text-[10px] font-mono px-2 py-0.5 ${style.bg} ${style.text} border ${style.border}`}>{grouped[s].length}</span>
            </div>
            <div className="space-y-2">
              {grouped[s].map((t) => (
                <Link
                  to={`/tasks/${t.id}`}
                  key={t.id}
                  className={`block border-l-4 ${style.border.replace("border-", "border-l-")} border-y border-r border-zinc-200 p-3 bg-white hover:border-[#002FA7] hover:shadow-sm transition-all cursor-pointer`}
                  data-testid={`task-card-${t.id}`}
                >
                  <div className="text-sm font-medium mb-1">{t.title}</div>
                  {t.description && <div className="text-xs text-zinc-500 mb-2 line-clamp-2">{t.description}</div>}
                  <div className="flex items-center justify-between text-xs">
                    <span className={`px-1.5 py-0.5 border ${
                      t.priority === "high" ? "border-red-300 text-red-700 bg-red-50" :
                      t.priority === "medium" ? "border-amber-300 text-amber-700 bg-amber-50" :
                      "border-zinc-300 text-zinc-600 bg-zinc-50"
                    }`}>{t.priority === "low" ? "normal" : t.priority}</span>
                    <span className="text-zinc-500 font-mono">{t.due_date || ""}</span>
                  </div>
                  <div className="text-[11px] mt-2 uppercase tracking-wider font-semibold flex items-center gap-1" style={{ color: "#EA580C" }}>
                    {userMap[t.assignee_id] || "Unassigned"}
                    {t.can_edit === false && (
                      <span className="text-zinc-400 ml-1 flex items-center gap-0.5"><Lock size={10} /> read-only</span>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {t.can_edit !== false && STATUSES.filter((x) => x !== s).map((x) => (
                      <button
                        key={x}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setStatus(t.id, x); }}
                        data-testid={`move-${t.id}-${x}`}
                        className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-zinc-300 hover:bg-zinc-50"
                      >→ {x.replace("_", " ")}</button>
                    ))}
                  </div>
                </Link>
              ))}
            </div>
          </div>
          );
        })}
      </div>

      <style>{`.input { width: 100%; border: 1px solid #d4d4d8; padding: 0.5rem 0.75rem; font-size: 0.875rem; outline: none; }
        .input:focus { border-color: #002FA7; box-shadow: 0 0 0 2px rgba(0,47,167,0.2); }`}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="label-uppercase block mb-1.5">{label}{required && " *"}</label>
      {children}
    </div>
  );
}
